import { CookieManager, logger, urlPool } from '@web_opxp/features';
import { LanguageService } from '@web_opxp/language';
import includes from 'lodash/includes';
import takeRight from 'lodash/takeRight';
import 'url-polyfill';
import { HistoryService, localStorage } from 'web_core_library';
import { ELogoutMode } from '../core/constants';
import * as ENV from '../core/env';

const UrlParameters = {
  // access token parameter - deprecated
  AccessToken: 'signin',
  // url where to return after login
  BackUrl: 'backUrl',
  // add signin to backUrl
  Forwarding: 'forwardAuth',
  // logout current user
  Logout: 'logout',
  // session token parameter
  SessionToken: 'session',
  // styling theme
  Theme: 'theme',
  // zendesk return_to parameter
  ReturnTo: 'return_to',
  // ob variant
  ObVariant: 'ob',
  // sso page variant
  SSOPage: 'sso',
  // mascot variant,
  MascotVariant: 'msct',
  // new ob flow approach
  OBFlow: 'ob_flow',
};

// all known non-tracking parameters which must be removed from url
const KnownUrlParameters = [
  UrlParameters.AccessToken,
  UrlParameters.BackUrl,
  UrlParameters.Forwarding,
  UrlParameters.Logout,
  UrlParameters.ReturnTo,
  UrlParameters.SessionToken,
  UrlParameters.Theme,
  UrlParameters.ObVariant,
];

const customWhitelist = ENV.BackUrlWhitelist;
export const BackUrlWhitelist = [
  'neuronation.com',
  'neuronation.de',
  'neuronation.fr',
  'neuronation.ru',
  ...(customWhitelist ? customWhitelist.split(',') : []),
];

export const OnboardingUrlWhitelist = ENV.OnboardingUrlWhitelist ? ENV.OnboardingUrlWhitelist.split(',') : [];

function addSlashToEnd(str = '') {
  str = str.replace(/\/$/, '');
  return str.indexOf('/?') >= 0 ? str : `${str}/`;
}

export function createUrl(externalUrl?: string) {
  const url = externalUrl || window.location.href;
  return new URL(url, window.location.origin);
}

export function getZendeskUrl(language: string, session?: string) {
  return urlPool.getZendeskUrl(language, session);
}

export const getBlogUrl = (lang?: string) => `${ENV.BlogPath}/${lang ?? 'de'}/`;

export const getEffectivenessUrl = (lang: string) => {
  return addSlashToEnd(HistoryService.getEffectivenessUrl(lang));
};

export const getJobsUrl = () => {
  return HistoryService.getJobsUrl();
};

export const getLegalsUrl = (page: string, lang?: string) => {
  const legalsPath = ENV.OnboardingAppPath === '/' ? '/legals' : `${ENV.OnboardingAppPath}/legals`;
  const pagePath = `${legalsPath}/${page}`;
  return HistoryService.getExternalUrl(pagePath, lang);
};

export const getPrivacyPolicyUrl = (lang?: string) => getLegalsUrl('dataprivacy', lang);

export const getTouUrl = (lang?: string) => getLegalsUrl('tou', lang);

export const getImprintUrl = (lang?: string) => getLegalsUrl('imprint', lang);

interface TSearchParamMap {
  [key: string]: string;
}

export function createUrlManager() {
  let params: TSearchParamMap = {};
  let backUrlSecure: boolean;

  function restore() {
    params = localStorage.load<TSearchParamMap>('params') || {};
  }

  function store() {
    localStorage.save('params', params);
  }

  function cleanup() {
    localStorage.remove('params');
  }

  function loadFromUrl() {
    const url = createUrl();
    url.searchParams.forEach((value: string, key: string) => {
      params[key] = value;
    });
  }

  function getSearchParam(param: string) {
    return params[param] || null;
  }

  function clearSearchParam(param: string) {
    if (params[param]) {
      delete params[param];
      store();
    }
  }

  function getBackUrl() {
    return getSearchParam(UrlParameters.BackUrl) ?? '';
  }

  function getLowLevelDomain(url: string) {
    // get domain from url
    const domain = createUrl(url).hostname;
    // split in parts
    const parts = domain.split('.');
    return parts[0];
  }

  function isSSO(url: string) {
    const domain = getLowLevelDomain(url);
    return /sso/i.test(domain);
  }

  function getTopLevelDomain(url: string) {
    // get domain from url
    const domain = createUrl(url).hostname;
    // split in parts
    const parts = domain.split('.');
    // take last two domains
    const lastParts = takeRight(parts, 2);
    // compile back to string
    return lastParts.join('.');
  }

  function checkBackUrl() {
    // security check for backUrl
    const backUrl = getBackUrl();
    // empty backUrl will be replaced with secure fallback url
    if (!backUrl) {
      return true;
    }
    // check if domain includes sso
    if (isSSO(backUrl)) {
      return false;
    }
    // check if domain is present in whitelist
    const topLevelDomain = getTopLevelDomain(backUrl);
    return includes(BackUrlWhitelist, topLevelDomain);
  }

  function isUrlInOnboardingWhitelist(url: string) {
    if (!url) {
      return false;
    }
    const domain = createUrl(url).hostname;
    return includes(OnboardingUrlWhitelist, domain);
  }

  function cleanUrl() {
    const url = createUrl();
    let paramsChanged = false;
    const searchParams = new URLSearchParams(url.search);
    url.searchParams.forEach((value: string, key: string) => {
      // delete all "known" parameters
      if (KnownUrlParameters.includes(key)) {
        searchParams.delete(key);
        paramsChanged = true;
      }
    });
    if (paramsChanged && HistoryService.isInited()) {
      HistoryService.updateSearchParams(searchParams);
    }
  }

  function getFallbackUrl() {
    return ENV.TrainingAppPath;
  }

  function getBackUrlWithFallback() {
    const backUrl = getBackUrl();
    const fallbackUrl = getFallbackUrl();
    // DO not change this to backUrl ?? fallbackUrl, because backUrl can be empty string, but it is still falsy value, and in this case we want to use fallbackUrl
    return backUrl ? backUrl : fallbackUrl;
  }

  function reload() {
    loadFromUrl();
    backUrlSecure = checkBackUrl();
    cleanUrl();
    store();
  }

  function isBackUrlSecure() {
    return backUrlSecure;
  }

  function redirect(url: string) {
    cleanup();
    window.location.assign(url);
  }

  function getUrlSearchParams() {
    return new URLSearchParams(params);
  }

  function removeLogout() {
    clearSearchParam(UrlParameters.Logout);
  }

  function getLogout() {
    return getSearchParam(UrlParameters.Logout);
  }

  function getAccessToken() {
    return getSearchParam(UrlParameters.AccessToken);
  }

  function removeAccessToken() {
    clearSearchParam(UrlParameters.AccessToken);
  }

  function getObFlow() {
    return getSearchParam(UrlParameters.OBFlow);
  }

  restore();
  loadFromUrl();
  // backUrl fallback
  if (!params[UrlParameters.BackUrl]) {
    // if backUrl was empty try to use referrer
    params[UrlParameters.BackUrl] = document.referrer || '';
  }
  backUrlSecure = checkBackUrl();
  cleanUrl();
  store();

  return {
    getBackUrl,
    getSearchParam,
    isBackUrlSecure,
    getBackUrlWithFallback,
    isUrlInOnboardingWhitelist,
    redirect,
    reload,
    getUrlSearchParams,
    clearSearchParam,
    removeLogout,
    getLogout,
    getAccessToken,
    removeAccessToken,
    getObFlow,
  };
}

export const urlManager = createUrlManager();

function getPasswordRedirect(url: URL) {
  const { hash, hostname, search } = url;
  const path = '/sso/opxp/password';
  return `https://${hostname}${path}${search}${hash}`;
}

export function createPasswordBackUrl() {
  const url = createUrl();
  const referrerUrl = urlManager.getBackUrl();
  referrerUrl && url.searchParams.set('backUrl', referrerUrl);
  const theme = 'opxp';
  url.searchParams.set('theme', theme);
  const lang = LanguageService.getLanguage() || 'de';
  lang && url.searchParams.set('lang', lang);
  const redirectUrl = getPasswordRedirect(url);
  return redirectUrl;
}

export const getSubscribeUrl = (lang: string, coupon?: string) => {
  const url = createUrl(addSlashToEnd(ENV.SubscribeAppPath));
  url.searchParams.set('lang', lang);
  coupon && url.searchParams.set('coupon', coupon);
  return url.href;
};

export const redirectBack = (session?: string, language?: string, loginFlow?: string, accessToken?: string) => {
  // try to redirect to backUrl if anything found in url
  const backUrl = urlManager.getBackUrlWithFallback();
  if (!backUrl) {
    console.warn('Back url is not defined. Nowhere to redirect');
    return;
  }
  const url = createUrl(backUrl);
  const forwardAuth = urlManager.getSearchParam(UrlParameters.Forwarding);
  // apply forwarding if a parameter given or if legacy url is set as backurl
  const shouldApplyForwarding = forwardAuth;
  if (shouldApplyForwarding) {
    const token = accessToken ?? '';
    // append `?signin=my-access-token` to the url
    url.searchParams.set(UrlParameters.AccessToken, token);
  }
  // add return_to if present
  const returnTo = urlManager.getSearchParam(UrlParameters.ReturnTo);
  returnTo && url.searchParams.set(UrlParameters.ReturnTo, returnTo);
  // add session token to url
  session && url.searchParams.set(UrlParameters.SessionToken, session);
  // See BW-3203
  // user might change language while on sso
  // but backUrl does not have language, so it will use one defined before
  // so we change language if it was changed since initial visit
  const langDefined = urlManager.getSearchParam('lang');
  const currentLang = language ?? LanguageService.getLanguage();
  if (langDefined && currentLang && langDefined !== currentLang) {
    url.searchParams.set('lang', currentLang);
  }
  // BW-6795 issue workaround: always set language for redirects to opxp
  const isNeuroNation = urlManager.isUrlInOnboardingWhitelist(backUrl);
  if (isNeuroNation && !!currentLang) {
    url.searchParams.set('lang', currentLang);
  }
  // BW-8542 add login parameter if defined to track successful login
  if (loginFlow) {
    url.searchParams.set('login', loginFlow);
  }
  const finalRedirectUrl = url.href;
  urlManager.redirect(finalRedirectUrl);
};

export function getLogoutDiscountUrl() {
  return '/logoutDiscount';
}

export function getPasswordForgotUrl() {
  return '/opxp/forgot';
}

export function getLogoutUrl(mode: ELogoutMode) {
  return `/opxp/logout/${mode}`;
}

export function getSignupUrl(step?: string, backUrl?: string, language?: string) {
  let finalRedirectUrl = '/opxp/signup';
  if (step) {
    finalRedirectUrl += `/${step}`;
  }
  const params: string[] = [];
  backUrl && params.push(`backUrl=${encodeURIComponent(backUrl)}`);
  language && params.push(`lang=${language}`);
  if (params.length > 0) {
    finalRedirectUrl += `/?${params.join('&')}`;
  }
  return finalRedirectUrl;
}

export function getSigninUrl(backUrl?: string, language?: string) {
  let finalRedirectUrl = '/opxp/login';
  const params: string[] = [];
  backUrl && params.push(`backUrl=${encodeURIComponent(backUrl)}`);
  language && params.push(`lang=${language}`);
  if (params.length > 0) {
    finalRedirectUrl = `${finalRedirectUrl}/?${params.join('&')}`;
  }
  return finalRedirectUrl;
}

export function getRefreshTokenHandler(opxp: boolean) {
  return () => {
    const opxpPrefix = opxp ? 'opxp' : '';
    const link = `/${opxpPrefix}/login`;
    HistoryService.push(link);
  };
}

export function redirectToSignup(couponToken?: string, language?: string) {
  const backUrlParam = urlManager.getBackUrl();
  const backUrl = backUrlParam ?? ENV.OnboardingAppAssessmentPath;
  const signupUrl = getSignupUrl(undefined, backUrl, language);
  const state = couponToken ? { couponToken } : undefined;
  HistoryService.push(signupUrl, state);
}

export function redirectToSignin(couponToken?: string, language?: string) {
  const backUrlParam = urlManager.getBackUrl();
  const backUrl = backUrlParam ?? ENV.TrainingAppPath;
  const signupUrl = getSigninUrl(backUrl, language);
  const state = couponToken ? { couponToken } : undefined;
  HistoryService.push(signupUrl, state);
}

export const isLogout = (search: string) => {
  const logoutCookie = CookieManager.getCookie(CookieManager.COOKIE_AUTH_LOGOUT);
  const logoutParam = urlManager.getLogout();
  const logoutSearch = search.indexOf('logout=') >= 0;
  logger(['isLogout', { logoutCookie, logoutParam, logoutSearch }]);
  if (!logoutParam && !logoutCookie && !logoutSearch) {
    return false;
  }
  return logoutParam === null ? ELogoutMode.Default : +logoutParam;
};

export const redirectExternal = (url: string, lang?: string, session?: string) => {
  return HistoryService.redirectExternal(url, lang, session);
};

export function getFullLocalUrl(path = '') {
  const basePath = ENV.BasePath;
  const relativePath = path.startsWith('/') ? path : `/${path}`;
  return `${basePath}${relativePath}`;
}
